<script setup lang="ts">
// Low-level file picker. Renders a hidden <input type="file"> and exposes
// `pick` / `clear` to the default slot — the caller composes its own visible
// trigger from kit citizens (Button, Card, etc.). v-model binds the chosen
// File or null. Caller can validate / format size via the slot's `file` arg.
import { ref } from 'vue'

interface Props {
  modelValue: File | null
  /** File-type filter — e.g. `.pdf,.png,image/*`. */
  accept?: string
}

defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: File | null): void
}>()

const inputRef = ref<HTMLInputElement | null>(null)

const pick = () => inputRef.value?.click()

const clear = () => {
  emit('update:modelValue', null)
  if (inputRef.value) inputRef.value.value = ''
}

const onChange = (event: Event) => {
  const input = event.target
  if (!(input instanceof HTMLInputElement)) return
  emit('update:modelValue', input.files?.[0] ?? null)
}
</script>

<template>
  <slot :pick="pick" :clear="clear" :file="modelValue" />
  <input
    ref="inputRef"
    type="file"
    :accept="accept"
    class="app-file-input"
    @change="onChange"
  />
</template>

<style scoped>
.app-file-input {
  display: none;
}
</style>
